import React, { useContext, useEffect, useState, useRef } from "react";
import {
  WebSocketContext,
} from "../context/WebSocketContext";
import Cards from "../components/Cards/Cards";
import { IoIosPeople } from "react-icons/io";
import { FaSmile } from "react-icons/fa";
import { LuChartNoAxesCombined } from "react-icons/lu";
import Engagement from "../components/Charts/Engagement";
import Queries from "../components/Charts/Queries";
import EscalatedQueries from "../components/Charts/EscalatedQueries";
import Table from "../components/Tables/Table";
import LiveView from "../components/LiveView/LiveView";
import BasicDateRange from "../components/BasicDateRange";
import axios from "axios";
import { localurl } from "../components/utils";
import ConversationClassification from "../components/Charts/ConversationClassification";
import WordsByScore from "../components/Bars/WordsByScore";
import WordFrequency from "../components/Bars/WordFrequency";
import dayjs from "dayjs";
function Dashboard() {
  const { messages } = useContext(WebSocketContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [customerInfo, setCustomerInfo] = useState([]);
  const [queries, setQueries] = useState({});
  const [trendData, setTrendData] = useState({});
  const [escalation, setEscalation] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(true);
  const datePickerRef = useRef(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isManuallyFiltered, setIsManuallyFiltered] = useState(false); // Track if user manually filtered
  const [chatSentiment, setchatSentiment] = useState({});
  const [topWords, setTopWords] = useState({});
  const [conversation, setConversation] = useState({});
  // Default range: start of this month to today
  const [selectedRange] = useState([dayjs().startOf("month"), dayjs()]);

  useEffect(() => {
    // Don't process dashboard WebSocket messages if user has manually filtered or is currently filtering
    // This prevents WebSocket's current month data from overwriting user's filtered date range
    if (isFiltering || isManuallyFiltered || messages.length === 0) {
      console.log("Dashboard: Skipping WebSocket update -", {
        isFiltering,
        isManuallyFiltered,
        messagesLength: messages.length
      });
      return;
    }

    // Find the most recent complete message (has both interaction and sentiments)
    // Only process if NOT manually filtered (to avoid overwriting filtered data with current month data)
    let relevantMessage = messages.findLast((msg) => msg.interaction && msg.sentiments);

    // If no complete message found, try to build one from partial messages
    if (!relevantMessage) {
      const latestInteractionMsg = messages.findLast((msg) => msg.interaction);
      const latestSentimentsMsg = messages.findLast((msg) => msg.sentiments);
      const latestCustomersMsg = messages.findLast((msg) => msg.customers);

      if (latestInteractionMsg || latestSentimentsMsg || latestCustomersMsg) {
        relevantMessage = {
          ...(latestInteractionMsg || {}),
          ...(latestSentimentsMsg || {}),
          ...(latestCustomersMsg || {}),
        };
      }
    }

    if (!relevantMessage || Object.keys(relevantMessage).length === 0) return;

    console.log("Dashboard: Setting sentiment data:", {
      chatSentiment: relevantMessage.sentiment_data?.chat_sentiment,
      topWords: relevantMessage.sentiment_data?.top_words_by_score,
      conversation: relevantMessage.sentiment_data?.conversation_classification
    });

    setDashboardData(relevantMessage);
    setQueries(relevantMessage.common_query || []);
    setEscalation(relevantMessage.escalation_queries || []);
    setCustomerInfo(relevantMessage.customers?.data || []);
    setTrendData(relevantMessage.daily_trend || []);
    setchatSentiment(relevantMessage.sentiment_data?.chat_sentiment || []);
    setTopWords(relevantMessage.sentiment_data?.top_words_by_score || []);
    setConversation(relevantMessage.sentiment_data?.conversation_classification || []);
  }, [messages, isFiltering, isManuallyFiltered]);

  const handleDateChange = async (newRange) => {
    if (!newRange[0] || !newRange[1]) return;
    setIsFiltering(true);

    // Check if this is the default range (current month) - if so, allow WebSocket updates
    const isDefaultRange =
      newRange[0].isSame(dayjs().startOf("month"), "day") &&
      newRange[1].isSame(dayjs(), "day");

    // Only block WebSocket updates if user selects a custom date range (not default)
    setIsManuallyFiltered(!isDefaultRange);

    console.log("Dashboard: Date filter changed -", {
      start: newRange[0].format("YYYY-MM-DD"),
      end: newRange[1].format("YYYY-MM-DD"),
      isDefaultRange,
      willBlockWebSocket: !isDefaultRange
    });

    // Use local date formatting to avoid UTC off-by-one shifts
    const start_date = newRange[0].format("YYYY-MM-DD");
    const end_date = newRange[1].format("YYYY-MM-DD");

    try {
      const response = await axios.get(`${localurl}/dashboard`, {
        params: { start_date, end_date },
        headers: {
          accept: "application/json",
        },
      });

      console.log("Dashboard API: Setting sentiment data from date filter:", {
        sentiment_data: response.data.sentiment_data,
        chatSentiment: response.data.sentiment_data?.chat_sentiment,
        topWords: response.data.sentiment_data?.top_words_by_score,
        conversation: response.data.sentiment_data?.conversation_classification
      });

      setDashboardData(response.data);
      setQueries(response.data.common_query || []);
      setEscalation(response.data.escalation_queries || []);
      setCustomerInfo(response.data.customers?.data || []);
      setTrendData(response.data.daily_trend || []);
      setchatSentiment(response.data.sentiment_data?.chat_sentiment || []);
      setTopWords(response.data.sentiment_data?.top_words_by_score || []);
      setConversation(response.data.sentiment_data?.conversation_classification || []);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      // Always reset filtering state
      setIsFiltering(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  // Trigger default date range fetch on mount
  useEffect(() => {
    // Don't mark as manually filtered for initial load - allow WebSocket updates
    const fetchInitialData = async () => {
      if (!selectedRange[0] || !selectedRange[1]) return;
      setIsFiltering(true);
      const start_date = selectedRange[0].format("YYYY-MM-DD");
      const end_date = selectedRange[1].format("YYYY-MM-DD");

      try {
        const response = await axios.get(`${localurl}/dashboard`, {
          params: { start_date, end_date },
          headers: {
            accept: "application/json",
          },
        });

        setDashboardData(response.data);
        setQueries(response.data.common_query || []);
        setEscalation(response.data.escalation_queries || []);
        setCustomerInfo(response.data.customers?.data || []);
        setTrendData(response.data.daily_trend || []);
        setchatSentiment(response.data.sentiment_data?.chat_sentiment || []);
        setTopWords(response.data.sentiment_data?.top_words_by_score || []);
        setConversation(response.data.sentiment_data?.conversation_classification || []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsFiltering(false);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col w-full relative max-md:overflow-x-hidden">
      <h1 className="text-black font-bold text-2xl mb-3">Dashboard Overview</h1>
      <p className="font-normal text-base text-gray-600 mb-10">
        Monitor your chatbot performance and user interactions
      </p>
      {showDatePicker && (
        <div className="flex justify-end mb-6 max-md:justify-start max-md:w-full max-md:overflow-x-hidden items-center gap-3">
          <div className="max-md:w-full max-md:min-w-0">
            <BasicDateRange
              onDateChange={handleDateChange}
              defaultRange={selectedRange}
            />
          </div>
          {isManuallyFiltered && (
            <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-md border border-blue-200">
              📅 Viewing filtered data (WebSocket updates paused)
            </div>
          )}
        </div>
      )}

      <div className="flex flex-row justify-between gap-5 max-lg:flex-col">
        <Cards
          category="Total Interactions"
          number={
            dashboardData?.interaction?.total_interaction_this_month ?? "--"
          }
          change={dashboardData?.interaction?.percentage_change_interactions}
          percent={
            dashboardData?.interaction?.total_interactions_last_month ??
            "..."
          }
          previous={
            dashboardData?.interaction?.total_interactions_last_month != null
              ? `vs ${dashboardData.interaction.total_interactions_last_month} last period`
              : " Interactions from last month"
          }
          percentColor="text-green"
          icon={<IoIosPeople />}
          iconColor="text-red-500"
        />
        <Cards
          category="Satisfaction Rate"
          number={
            dashboardData?.sentiments?.current_month_satisfaction_rate ?? "--"
          }
          change={dashboardData?.sentiments?.percentage_change}
          percent={
            dashboardData?.sentiments?.last_month_satisfaction_rate != null
              ? dashboardData.sentiments.last_month_satisfaction_rate + "%"
              : "..."
          }
          percentColor="text-green"
          icon={<FaSmile />}
          previous={
            dashboardData?.sentiments?.last_month_satisfaction_rate != null
              ? `vs ${dashboardData.sentiments.last_month_satisfaction_rate}% last period`
              : "from last month"
          }
          iconColor="text-yellow-500"
        />
        <Cards
          category="Escalation Rate"
          number={
            dashboardData?.escalation_rate?.current_month_escalation_rate ??
            "--"
          }
          percent={
            dashboardData?.escalation_rate?.last_month_escalation_rate != null
              ? dashboardData.escalation_rate.last_month_escalation_rate + "%"
              : "..."
          }
          percentColor="text-red-500"
          icon={<LuChartNoAxesCombined />}
          iconColor="text-pink-500"
          previous={
            dashboardData?.escalation_rate?.last_month_escalation_rate != null
              ? `vs ${dashboardData.escalation_rate.last_month_escalation_rate}% last period`
              : "from last month"
          }
        />
      </div>

      <div className="flex flex-row justify-between gap-5 mt-10 max-lg:flex-col">
        <Engagement data={trendData} chartName="Engagement Trends" />
        <Queries commonQueries={queries} pieName="Common user queries" />
        <EscalatedQueries data={escalation} />
      </div>

      <div className="mt-10 shadow-custom">
        <Table
          columns={[
            { Header: "ID", accessor: "id" },
            { Header: "IP Address", accessor: "ip" },
            { Header: "Country", accessor: "country" },
            { Header: "Region", accessor: "region" },
            { Header: "City", accessor: "city" },
            { Header: "Total Queries", accessor: "total_queries" },
          ]}
          customers={customerInfo}
        />
      </div>

      <div className="mt-10 shadow-custom">
        <LiveView />
      </div>
      <div className="flex flex-row justify-between gap-5 mt-10 max-lg:flex-col">
        <WordFrequency sentiment={chatSentiment} />
        <ConversationClassification conversation={conversation} />
        <WordsByScore topWords={topWords} />
      </div>
    </div>
  );
}

export default Dashboard;
